import { Inject, Injectable } from '@angular/core'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { environment } from '../../environments/environment'
import { connectFunctionsEmulator, Functions } from '@angular/fire/functions'
import { DOCUMENT } from '@angular/common'
import { GlobalService } from './global.service'
import {
    Client,
    merchantParkingsPath,
    OmnisendAddToCartRequest,
    OmnisendCreateCartRequest,
    OmnisendCreateOrderRequest,
    OmnisendCreateProductRequest,
    OmnisendProduct,
    OmnisendVariant,
    Parking,
    OmnisendCreateContactResponse,
} from '@parkupp/core'
import { ClientService } from './client.service'
import { AuthenticationService } from './authentication.service'
import * as moment from 'moment'
import { doc, updateDoc } from '@angular/fire/firestore'

@Injectable({
    providedIn: 'root',
})
export class OmnisendService {
    private readonly COOKIE_NAME = 'omnisendContactID'
    functions: Functions
    private window: any

    constructor(@Inject(DOCUMENT) private document: Document, private globalService: GlobalService, private authService: AuthenticationService, private clientService: ClientService) {
        this.functions = getFunctions()
        if (environment.useEmulators) {
            connectFunctionsEmulator(this.functions, 'localhost', 5001)
        }
        this.window = this.document.defaultView

        this.clientService.activeClientReady.subscribe((client) => {
            this.updateUserCookie(client as Client)
        })

        this.authService.onSignOut.subscribe(() => {
            this.globalService.deleteCookie(this.COOKIE_NAME)
        })
    }

    async productView(parking: Parking) {
        try {
            if (!parking.omnisendProductId) {
                const result = await this.createProduct(parking)
                const productId = result?.data?.data?.id || parking.$key

                const update = {
                    omnisendProductId: productId,
                    updatedAt: new Date(),
                }
                const docRef = doc(this.clientService.firestore, merchantParkingsPath(parking.merchantRef.id), parking.$key!)
                await updateDoc(docRef, update)
            }

            const productData = {
                $productID: parking.$key,
                $currency: 'ZAR',
                $price: parking.defaultPriceInCents,
                $title: parking.title,
                $variantID: parking.$key,
                $imageUrl: parking.defaultImage,
                $productUrl: `${environment.host}/parking/${parking.$key}`,
            }
            this.window.omnisend.push(['track', '$productViewed', productData])
        } catch (error) {
            console.error('[Omnisend] Error in productView:', error)
            throw error
        }
    }

    updateUserCookie(client: Client) {
        if (!client) return

        const existingContactId = this.globalService.getCookie(this.COOKIE_NAME)

        if (existingContactId && client.omnisendContactKey === existingContactId) return

        if (client.omnisendContactKey) {
            this.globalService.setCookie({
                name: this.COOKIE_NAME,
                value: client.omnisendContactKey,
                expireDays: 30,
            })
        } else {
            this.createContact({
                firstName: client.firstName,
                lastName: client.lastName,
                phone: client.$phoneNumber,
                email: client.$email,
                clientKey: client.$key!,
            })
        }
    }

    async createContact(data: { firstName: any; lastName: any; phone: string; email: string; clientKey: string }) {
        try {
            const createContact = httpsCallable<typeof data, OmnisendCreateContactResponse>(this.functions, 'omnisend-createContact')
            const result = await createContact(data)
            if (result.data.contactID) {
                this.globalService.setCookie({
                    name: this.COOKIE_NAME,
                    value: result.data.contactID,
                    expireDays: 30,
                })
                await this.clientService.updateClient({ omnisendContactKey: result.data.contactID }, data.clientKey)
            }
            return result.data
        } catch (error) {
            console.error('[Omnisend] Contact creation failed:', error)
            throw error
        }
    }

    async getSetCart(req: { activeClient: Client; subscriptionKey: string; parking: Parking; quantity: number; priceInCents: number; orderTotal: number }) {
        if (req.activeClient.omnisendCartKey) {
            try {
                const deleteCart = httpsCallable(this.functions, 'omnisend-deleteCart')
                await deleteCart({ cartId: req.activeClient.omnisendCartKey, email: req.activeClient.$email })
                await this.createCart(req)
            } catch (error) {
                console.error('[Omnisend] Cart deletion failed:', error)
                await this.createCart(req)
            }
        } else {
            await this.createCart(req)
        }
    }

    async createCart(req: { activeClient: Client; subscriptionKey: string; parking: Parking; quantity: number; priceInCents: number; orderTotal: number }) {
        const data = new OmnisendCreateCartRequest({
            productID: req.parking.$key,
            variantID: req.parking.$key,
            title: req.parking.title,
            description: req.parking.description?.substring(0, 300),
            productUrl: `${environment.host}/parking/${req.parking.$key}`,
            cartID: req.subscriptionKey,
            email: req.activeClient.$email,
            phone: req.activeClient.$phoneNumber,
            contactID: req.activeClient.omnisendContactKey,
            cartRecoveryUrl: `${environment.host}/cart/${req.subscriptionKey}`,
            subscriptionKey: req.subscriptionKey,
            quantity: req.quantity,
            priceInCents: req.parking.defaultPriceInCents / 100,
            imageUrl: req.parking.defaultImage,
        })

        try {
            const createCart = httpsCallable(this.functions, 'omnisend-createCart')
            const result = await createCart(data)
            await this.clientService.updateClient({ omnisendCartKey: req.subscriptionKey }, req.activeClient.$key!)
            await this.addToCart(req)
            return result
        } catch (error) {
            console.error('[Omnisend] Cart creation failed:', error)
            throw error
        }
    }

    async addToCart(req: { activeClient: Client; subscriptionKey: string; parking: Parking; quantity: number; priceInCents: number; orderTotal: number }) {
        const omnisendAddToCartRequest = new OmnisendAddToCartRequest({
            currency: 'ZAR',
            cartProductID: req.parking.$key,
            productID: req.parking.$key,
            variantID: req.parking.$key,
            title: req.parking.title,
            description: req.parking.description?.substring(0, 300),
            quantity: req.quantity,
            price: req.priceInCents / 100,
            productUrl: `${environment.host}/parking/${req.parking.$key}`,
            imageUrl: req.parking.defaultImage,
        })

        try {
            const addToCart = httpsCallable(this.functions, 'omnisend-addToCart')
            const result = await addToCart({ cartId: req.subscriptionKey, omnisendAddToCartRequest })
            return result
        } catch (error) {
            console.error('[Omnisend] Add to cart failed:', error)
            throw error
        }
    }

    async createProduct(parking: Parking): Promise<{ data: { data: { id: string } } }> {
        if (!parking.defaultImage) {
            throw new Error('Parking must have a default image')
        }

        const omnisendVariant = new OmnisendVariant({
            id: parking.$key,
            title: parking.title,
            status: 'inStock',
            price: parking.defaultPriceInCents / 100,
            url: `${environment.host}/parking/${parking.$key}`,
            imageID: 'Image001',
        })

        const omnisendCreateProductRequest = new OmnisendCreateProductRequest({
            id: parking.$key,
            title: parking.title,
            status: 'inStock',
            description: parking.description?.substring(0, 300),
            currency: 'ZAR',
            url: `${environment.host}/parking/${parking.$key}`,
            type: parking.niceType,
            createdAt: moment().toISOString(),
            images: [parking.defaultImage],
            variants: [omnisendVariant],
        })

        try {
            const createProduct = httpsCallable<OmnisendCreateProductRequest, { data: { id: string } }>(this.functions, 'omnisend-createProduct')
            const result = await createProduct(omnisendCreateProductRequest)
            return result
        } catch (error) {
            console.error('[Omnisend] Product creation failed:', error)
            throw error
        }
    }

    async createOrder(req: { activeClient: Client; subscriptionKey: string; parking: Parking; quantity: number; priceInCents: number; orderTotal: number }) {
        const product = new OmnisendProduct({
            cartProductID: req.parking.$key,
            productID: req.parking.$key,
            variantID: req.parking.$key,
            title: req.parking.title,
            description: req.parking.description?.substring(0, 300),
            quantity: req.quantity,
            price: req.priceInCents / 100,
            productUrl: `${environment.host}/parking/${req.parking.$key}`,
            imageUrl: req.parking.defaultImage,
        })

        const requestData = new OmnisendCreateOrderRequest({
            orderID: req.subscriptionKey,
            email: req.activeClient.$email,
            phone: req.activeClient.$phoneNumber,
            firstName: req.activeClient.firstName,
            lastName: req.activeClient.lastName,
            cartID: req.subscriptionKey,
            orderUrl: `${environment.host}/client/${req.activeClient.$key}/subscription/${req.subscriptionKey}`,
            currency: 'ZAR',
            orderSum: req.orderTotal / 100,
            createdAt: moment().toISOString(),
            updatedAt: moment().toISOString(),
            paymentMethod: 'Credit card',
            paymentStatus: 'paid',
            fulfillmentStatus: 'fulfilled',
            products: [product],
        })

        try {
            const createOrder = httpsCallable(this.functions, 'omnisend-createOrder')
            const result = await createOrder(requestData)
            return result
        } catch (error) {
            console.error('[Omnisend] Order creation failed:', error)
            throw error
        }
    }
}
